import React, { useContext } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useHasRole } from "@homesusa/auth";
import {
  Format,
  RoleUser,
  RoleUserLabel,
  Phone,
  PhoneType,
  RoleEmployee,
  SectionBox
} from "@homesusa/core";
import {
  Select,
  FormStyled,
  FormContext,
  Input,
  PhoneInput,
  Switch,
  FormSectionTitle
} from "@homesusa/form";
import { Button, AppContext } from "@homesusa/layout";
import { AnimatedContainer } from "core/components";
import { UserContext, UserFormContext } from "../contexts";
import { UserPasswordProvider } from "../providers";
import { upsertPhoneByType } from "../utils/phones.util";
import SecurityFormAdmin from "./security-form-admin.component";

function UserForm(): JSX.Element {
  const { isMlsAdministrator, hasEmployeeRole } = useHasRole();
  const { formState, formDispatch } =
    React.useContext<UserFormContext>(FormContext);
  const { user: userContext } = React.useContext(UserContext);

  const {
    appState: { apps }
  } = useContext(AppContext);

  const options = React.useMemo(
    () =>
      Object.values(RoleUser).reduce(
        (a, x) => ({
          ...a,
          [x]: RoleUserLabel.get(x)
        }),
        {}
      ),
    []
  );
  const handleChange = (value: unknown, name: string): void => {
    if (!formDispatch) {
      return;
    }
    formDispatch({
      type: "FormChangeByInputName",
      inputName: name,
      payload: value
    });
  };
  const multiselectChange = (value: unknown): void => {
    const newRolesValues = [value];
    handleChange(newRolesValues, "roles");
  };
  const phonesChange = (phone: Phone): void => {
    handleChange(upsertPhoneByType(formState.phones, phone), "phones");
  };

  const findPhoneByType = (type: string): string | undefined =>
    formState.phones?.find((phone) => phone.phoneType === type)?.number;
  return (
    <AnimatedContainer>
      <FormStyled>
        <Box>
          <FormSectionTitle title="User Details" sx={{ my: 3 }} />
          <Grid container columnSpacing={4}>
            <Grid item xs={6}>
              <SectionBox title="General">
                <Grid container>
                  <Grid item xs={6}>
                    <Input name="firstName" />
                  </Grid>
                  <Grid item xs={6}>
                    <Input name="lastName" />
                  </Grid>
                  <Grid item xs={6}>
                    <PhoneInput
                      name="businessPhone"
                      id="businessPhone"
                      label="Business Phone"
                      onChange={(value): void =>
                        phonesChange({
                          number: value,
                          phoneType: PhoneType.Business
                        })
                      }
                      defaultValue={findPhoneByType(PhoneType.Business)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <PhoneInput
                      name="mobilePhone"
                      id="mobilePhone"
                      label="Mobile Phone"
                      onChange={(value): void =>
                        phonesChange({
                          number: value,
                          phoneType: PhoneType.Mobile
                        })
                      }
                      defaultValue={findPhoneByType(PhoneType.Mobile)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Input label="Username" name="email" disabled />
                  </Grid>
                  <Grid item xs={6}>
                    <Input
                      label="Created On"
                      required
                      defaultValue={Format.DateTime(formState.sysCreatedOn)}
                      disabled
                    />
                  </Grid>
                </Grid>
              </SectionBox>
            </Grid>
            {(isMlsAdministrator ||
              hasEmployeeRole([RoleEmployee.CompanyAdmin])) && (
              <Grid container item xs={6}>
                {isMlsAdministrator && (
                  <Grid item xs={6}>
                    <Box>
                      <SectionBox title="Role Information">
                        <Grid container>
                          <Grid item xs={12}>
                            <Select
                              label="Role"
                              defaultValue={
                                formState.roles ? formState.roles[0] : undefined
                              }
                              options={options}
                              onChange={multiselectChange}
                              removeBlankOption
                              required
                            />
                          </Grid>

                          {userContext.roles?.includes(
                            RoleUser.Photographer
                          ) && (
                            <Grid item xs={12}>
                              <Button
                                LinkComponent="a"
                                href={`${apps.photoService}/photographers/${userContext.id}`}
                              >
                                Photography Services
                              </Button>
                            </Grid>
                          )}
                        </Grid>
                      </SectionBox>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Box>
                    <SectionBox title="Email Confirmed">
                      <Grid container>
                        <Grid item xs={12}>
                          <Switch
                            defaultValue={formState.emailConfirmed}
                            name="emailConfirmed"
                            label="Confirmed"
                            disabled={formState.emailConfirmed}
                          />
                        </Grid>
                      </Grid>
                    </SectionBox>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Box>
        <Box>
          <FormSectionTitle title="User Password" sx={{ my: 3 }} />
          <UserPasswordProvider userId={formState.id}>
            <SecurityFormAdmin />
          </UserPasswordProvider>
        </Box>
      </FormStyled>
    </AnimatedContainer>
  );
}

export default UserForm;
